import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Typography, Button, IconButton, CircularProgress,
  Chip, Stack, Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as DoneIcon,
  RadioButtonUnchecked as OpenIcon,
  Task as TaskIcon,
} from '@mui/icons-material'
import {
  salesApi, SalesTask, CreateSalesTaskPayload, UpdateSalesTaskPayload,
} from '../../lib/api/salesApi'
import { toast } from 'react-toastify'
import { themeColors } from '../../app/layout/theme'
import { TaskModal } from '../shared/TaskModal'

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(iso: string | null) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const PRIORITY_COLORS: Record<string, { bg: string; color: string }> = {
  LOW: { bg: '#f0fdf4', color: '#16a34a' },
  MEDIUM: { bg: '#fefce8', color: '#ca8a04' },
  HIGH: { bg: '#fff7ed', color: '#ea580c' },
  URGENT: { bg: '#fef2f2', color: '#dc2626' },
}

const PRIORITY_LABELS: Record<string, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  URGENT: 'Urgent',
}

const STATUS_LABELS: Record<string, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  DONE: 'Done',
}

// ── Task Card ─────────────────────────────────────────────────────────────────

interface TaskCardProps {
  salesId: string
  task: SalesTask
  canModify?: boolean
  onEdit: (task: SalesTask) => void
}

function TaskCard({ salesId, task, canModify = true, onEdit }: TaskCardProps) {
  const qc = useQueryClient()
  const isDone = task.status === 'DONE'

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['sales-tasks', salesId] })
    qc.invalidateQueries({ queryKey: ['sales-activities', salesId] })
  }

  const toggleMut = useMutation({
    mutationFn: () => salesApi.updateTask(salesId, task.id, {
      title: task.title,
      status: isDone ? 'OPEN' : 'DONE',
      priority: task.priority,
    } as UpdateSalesTaskPayload),
    onSuccess: () => {
      invalidate()
      toast.success(isDone ? 'Task reopened' : 'Task completed!')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  })

  const deleteMut = useMutation({
    mutationFn: () => salesApi.deleteTask(salesId, task.id),
    onSuccess: () => {
      invalidate()
      toast.success('Task deleted')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete task'),
  })

  const pc = PRIORITY_COLORS[task.priority] ?? PRIORITY_COLORS.MEDIUM
  const dueDate = fmt(task.dueDate)
  const isOverdue = task.dueDate && !isDone && new Date(task.dueDate) < new Date()

  return (
    <Box sx={{
      p: 1.5, border: `1px solid ${themeColors.border}`, borderRadius: '10px',
      bgcolor: isDone ? '#f9fafb' : '#fff',
      opacity: isDone ? 0.75 : 1,
      transition: 'all 0.15s',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {/* Toggle button */}
        <Tooltip title={isDone ? 'Reopen' : 'Mark done'}>
          <IconButton size="small" onClick={() => toggleMut.mutate()}
            disabled={toggleMut.isPending || !canModify}
            sx={{ color: isDone ? '#16a34a' : '#9ca3af', mt: '-2px', p: 0.25 }}>
            {isDone
              ? <DoneIcon sx={{ fontSize: 20, color: '#16a34a' }} />
              : <OpenIcon sx={{ fontSize: 20, color: '#d1d5db' }} />
            }
          </IconButton>
        </Tooltip>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize="13px" fontWeight={600}
            sx={{ textDecoration: isDone ? 'line-through' : 'none', color: isDone ? 'text.secondary' : 'text.primary' }}>
            {task.title}
          </Typography>
          {task.description && (
            <Typography fontSize="12px" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
              {task.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label={PRIORITY_LABELS[task.priority] ?? task.priority} size="small"
              sx={{
                height: 18, fontSize: '10px', fontWeight: 700, bgcolor: pc.bg, color: pc.color,
                borderRadius: '4px', '& .MuiChip-label': { px: '6px' }
              }} />
            <Chip label={STATUS_LABELS[task.status] ?? task.status} size="small"
              sx={{
                height: 18, fontSize: '10px', fontWeight: 700, borderRadius: '4px',
                bgcolor: isDone ? '#f0fdf4' : '#eff6ff',
                color: isDone ? '#16a34a' : '#2563eb',
                '& .MuiChip-label': { px: '6px' }
              }} />
            {dueDate && (
              <Typography fontSize="10.5px"
                sx={{ color: isOverdue ? '#dc2626' : '#6b7280', fontWeight: isOverdue ? 700 : 400 }}>
                {isOverdue ? '⚠ Overdue · ' : 'Due: '}{dueDate}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Actions */}
        {canModify && (
          <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
            <IconButton size="small" onClick={() => onEdit(task)} sx={{ color: '#9ca3af' }}>
              <EditIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <IconButton size="small" onClick={() => deleteMut.mutate()}
              disabled={deleteMut.isPending} sx={{ color: '#9ca3af' }}>
              <DeleteIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function SalesTasksSection({ salesId, canModify = true }: { salesId: string; canModify?: boolean }) {
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editTask, setEditTask] = useState<SalesTask | null>(null)

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['sales-tasks', salesId],
    queryFn: () => salesApi.getTasks(salesId),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['sales-tasks', salesId] })
    qc.invalidateQueries({ queryKey: ['sales-activities', salesId] })
  }

  const createMut = useMutation({
    mutationFn: (p: CreateSalesTaskPayload) => salesApi.addTask(salesId, p),
    onSuccess: () => {
      invalidate()
      setModalOpen(false)
      toast.success('Task added')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to add task'),
  })

  const updateMut = useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: UpdateSalesTaskPayload }) =>
      salesApi.updateTask(salesId, taskId, payload),
    onSuccess: () => {
      invalidate()
      setEditTask(null)
      toast.success('Task updated')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update task'),
  })

  const open = tasks.filter(t => t.status !== 'DONE')
  const done = tasks.filter(t => t.status === 'DONE')

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography fontSize="13px" color="text.secondary">
          {open.length} open · {done.length} completed
        </Typography>
        {canModify && (
          <Button size="small" startIcon={<AddIcon />} variant="outlined"
            onClick={() => setModalOpen(true)}
            sx={{
              borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '12px',
              borderColor: themeColors.border, color: 'text.primary'
            }}>
            Add Task
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      ) : tasks.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#f9fafb', borderRadius: '12px', border: '1px dashed #e5e7eb' }}>
          <TaskIcon sx={{ fontSize: 32, color: '#d1d5db', mb: 1 }} />
          <Typography fontSize="13px" color="text.secondary">
            {!canModify ? 'No tasks were added to this sales record.' : 'No tasks yet. Add one to track post-sales execution.'}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {/* Open tasks */}
          {open.map(task => <TaskCard key={task.id} salesId={salesId} task={task} canModify={canModify} onEdit={setEditTask} />)}

          {/* Completed tasks */}
          {done.length > 0 && (
            <>
              <Typography fontSize="11px" fontWeight={700} color="text.disabled"
                textTransform="uppercase" letterSpacing="0.5px" sx={{ pt: 1 }}>
                Completed ({done.length})
              </Typography>
              {done.map(task => <TaskCard key={task.id} salesId={salesId} task={task} canModify={canModify} onEdit={setEditTask} />)}
            </>
          )}
        </Stack>
      )}

      {/* Modals */}
      <TaskModal
        open={modalOpen}
        entityType="sales"
        onClose={() => setModalOpen(false)}
        onSave={p => createMut.mutate(p as CreateSalesTaskPayload)}
        isSaving={createMut.isPending}
      />
      <TaskModal
        open={editTask !== null}
        task={editTask}
        entityType="sales"
        onClose={() => setEditTask(null)}
        onSave={p => editTask && updateMut.mutate({ taskId: editTask.id, payload: p as UpdateSalesTaskPayload })}
        isSaving={updateMut.isPending}
      />
    </Box>
  )
}
